import { useEffect, useRef } from "react";
import { useRatioCompliance } from "@/hooks/useRatioCompliance";
import { useAuth } from "@/contexts/AuthContext";
import { useNurseryId } from "@/hooks/useNurseryId";
import { useToast } from "@/hooks/use-toast";
import type { BreachType } from "@/lib/ratioEngine";
import { friendlyError } from "@/lib/friendlyError";

/**
 * Shows an on-screen warning when a live ratio rule breaks. It does NOT save
 * anything: the server check (cron-ratio-check) is the only writer of
 * `ratio_breaches`, so stored start/cleared times never depend on whether
 * someone had the app open.
 */
export function useRatioBreachWatcher() {
  const { zones } = useRatioCompliance();
  const { user } = useAuth();
  const nurseryId = useNurseryId();
  const { toast } = useToast();
  const lastStateRef = useRef<Record<string, Set<BreachType>>>({});
  const notifiedRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!nurseryId || !user) return;

    zones.forEach(async (z) => {
      const prev = lastStateRef.current[z.zoneId] ?? new Set<BreachType>();
      const curr = new Set<BreachType>(z.breachTypes);

      // Newly opened breaches
      for (const bt of curr) {
        if (prev.has(bt)) continue;
        const notifyKey = `${z.zoneId}:${bt}`;
        if (!notifiedRef.current.has(notifyKey)) {
          notifiedRef.current.add(notifyKey);
          toast({
            title: "Ratio breach",
            description: friendlyError(z),
            variant: "destructive",
          });
        }
      }

      // Newly resolved breaches
      for (const bt of prev) {
        if (curr.has(bt)) continue;
        notifiedRef.current.delete(`${z.zoneId}:${bt}`);
      }

      lastStateRef.current[z.zoneId] = curr;
    });
  }, [zones, nurseryId, user, toast]);
}
